import { defineStore } from "pinia";
import { ref } from "vue";
import { fetchCurrentUserInfo } from "@app/api/user";
import { useAuthStore } from "@shared/store/modules/auth";
import type { CurrentUserInfoData, UserDetail, UserMenuItem } from "@shared/types/user";

/** 后台当前用户（会话内缓存；menus 由路由守卫拉 info 时同步水合） */
export const useCurrentUserStore = defineStore("currentUser", () => {
    const userDetail = ref<UserDetail | null>(null);
    const menus = ref<UserMenuItem[]>([]);
    const loaded = ref(false);
    const loading = ref(false);
    let inFlight: Promise<void> | null = null;

    /** 同步顶栏展示名与头像 */
    function syncAuthDisplay(detail: UserDetail | null) {
        if (!detail) return;
        const name = detail.username || detail.nick || detail.tel || "";
        if (!name && !(detail.avatar || "").trim()) return;
        const auth = useAuthStore();
        auth.setUserInfo({
            id: detail.id,
            name: name || auth.userInfo?.name || "",
            avatar: (detail.avatar || "").trim() || undefined,
            tel: detail.tel,
            email: detail.email,
            roles: auth.userInfo?.roles ?? [],
        });
    }

    function applyInfo(data: CurrentUserInfoData) {
        userDetail.value = data.userDetail ?? null;
        syncAuthDisplay(userDetail.value);
        loaded.value = true;
    }

    function setMenus(items: UserMenuItem[]) {
        menus.value = items;
    }

    /** 路由守卫拉菜单时复用同一次 info，避免顶栏与菜单各打一遍 */
    function hydrateFromInfo(data: CurrentUserInfoData) {
        applyInfo(data);
    }

    async function fetchInfo(force = false) {
        if (loading.value && inFlight) {
            return inFlight;
        }
        if (loaded.value && !force) {
            return;
        }

        loading.value = true;
        inFlight = (async () => {
            try {
                const data = await fetchCurrentUserInfo();
                applyInfo(data);
            } catch (e) {
                // 刷新失败保留会话缓存，避免保存资料后顶栏/菜单被误清
                console.error("[currentUser]", e);
            } finally {
                loading.value = false;
                inFlight = null;
            }
        })();

        return inFlight;
    }

    function fetchIfNeeded() {
        return fetchInfo(false);
    }

    function refresh() {
        return fetchInfo(true);
    }

    function reset() {
        userDetail.value = null;
        menus.value = [];
        loaded.value = false;
        loading.value = false;
        inFlight = null;
    }

    return { userDetail, menus, loaded, loading, hydrateFromInfo, setMenus, fetchIfNeeded, refresh, reset };
});
